import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError, formatIndian } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { History, Trash2, AlertTriangle, FileSpreadsheet, Info, RotateCcw } from "lucide-react";

const SOURCE_TYPES = [
  { v: "__all__", l: "All sources" },
  { v: "ho", l: "Head Office" },
  { v: "franchise", l: "Franchise" },
  { v: "distributor", l: "Distributor" },
  { v: "unassigned", l: "Unassigned" },
];

export default function UploadHistory() {
  const { user } = useAuth();
  const canDelete = user?.role === "master_admin";
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ source_type: "__all__", is_memo: "__all__", status: "__all__", date_from: "", date_to: "" });
  const [deleting, setDeleting] = useState(null);
  const [impact, setImpact] = useState(null);
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    const q = new URLSearchParams();
    if (filters.source_type !== "__all__") q.set("source_type", filters.source_type);
    if (filters.is_memo !== "__all__") q.set("is_memo", filters.is_memo);
    if (filters.status !== "__all__") q.set("status", filters.status);
    if (filters.date_from) q.set("date_from", filters.date_from);
    if (filters.date_to) q.set("date_to", filters.date_to);
    try {
      const { data } = await api.get(`/upload-batches?${q}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      setItems([]);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDelete = async (b) => {
    setDeleting(b);
    setImpact(null);
    setStep(1);
    setConfirmText("");
    try {
      const { data } = await api.get(`/upload-batches/${b.id}/deletion-impact`);
      setImpact(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (confirmText.trim() !== deleting.filename) {
      toast.error("Filename does not match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/upload-batches/${deleting.id}`);
      toast.success(`Batch rolled back: ${data.deleted} deleted, ${data.reverted} reverted`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const sourceLabel = (b) => {
    if (b.source_type === "ho") return "Head Office Stock (HO)";
    if (b.source_type === "unassigned") return "Unassigned";
    const tag = b.source_type === "franchise" ? "Franchise" : "Distributor";
    return `${b.source_name || "—"} (${tag})`;
  };

  return (
    <div className="space-y-8" data-testid="upload-history-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">History</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Upload History</h1>
        <p className="text-slate-500 mt-2">Track every product upload and revert if needed.</p>
      </div>

      <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300" data-testid="parser-fix-banner">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>Net Weight Parser Fix (CS 17):</strong> Products uploaded before 19 May 2026 may have understated Net Weight if they
            included multi-row metal components. To recalculate accurately, rollback the affected batch and re-upload with the same Excel
            file — the updated parser will correctly sum weights across all rows.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="luxe-card p-5 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="upload-history-filters">
        <Select value={filters.source_type} onValueChange={(v) => setFilters({ ...filters, source_type: v })}>
          <SelectTrigger className="h-10 bg-white border-[#0D1B2A]/15"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            {SOURCE_TYPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.is_memo} onValueChange={(v) => setFilters({ ...filters, is_memo: v })}>
          <SelectTrigger className="h-10 bg-white border-[#0D1B2A]/15"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            <SelectItem value="true">Memo</SelectItem>
            <SelectItem value="false">Sales</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="h-10 bg-white border-[#0D1B2A]/15"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="rolled_back">Rolled Back</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-10" />
        <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-10" />
      </div>

      {/* List */}
      <div className="space-y-4" data-testid="upload-history-list">
        {items === null && Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-32" />)}
        {items && items.length === 0 && (
          <div className="luxe-card p-12 text-center text-slate-400">
            <History className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
            <p>No uploads match the current filters.</p>
          </div>
        )}
        {items && items.map((b) => (
          <div key={b.id} className="luxe-card p-6" data-testid={`upload-batch-${b.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <FileSpreadsheet className="w-4 h-4 text-[#C9A84C]" strokeWidth={1.5} />
                  <span className="font-medium text-[#0D1B2A] break-all">{b.filename}</span>
                  {b.is_memo && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Memo</span>}
                  {b.status === "rolled_back" && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 inline-flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Rolled back
                    </span>
                  )}
                </div>
                <div className="text-sm text-slate-500">
                  <span className="text-[#0D1B2A]">Source:</span> {sourceLabel(b)}
                </div>
                <div className="text-xs text-slate-400">
                  Uploaded: {formatDate(b.uploaded_at)} by {b.uploaded_by_email || b.uploaded_by}
                </div>
                <div className="text-xs tabular-nums">
                  <span className="text-emerald-600">{formatIndian(b.products_added)} added</span>
                  {" · "}
                  <span className="text-sky-600">{formatIndian(b.products_updated)} updated</span>
                  {" · "}
                  <span className="text-slate-500">{formatIndian(b.products_skipped)} skipped</span>
                </div>
                {b.status === "rolled_back" && (
                  <div className="text-[11px] text-slate-400 italic">
                    Rolled back {formatDate(b.rolled_back_at)} by {b.rolled_back_by}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 ml-auto">
                {canDelete && b.status === "completed" && (
                  <Button onClick={() => openDelete(b)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`delete-batch-${b.id}`}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Batch
                  </Button>
                )}
                {b.status === "rolled_back" && (
                  <span className="text-xs text-slate-400 italic px-3 py-2">Already rolled back</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two-step delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o && !busy) { setDeleting(null); setImpact(null); setStep(1); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="delete-batch-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" /> Delete this upload batch?
            </DialogTitle>
          </DialogHeader>
          {step === 1 && deleting && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-600">
                This will affect <strong>{(impact?.to_delete_count || 0) + (impact?.to_revert_count || 0)}</strong> product
                {((impact?.to_delete_count || 0) + (impact?.to_revert_count || 0)) === 1 ? "" : "s"} that were added or updated in this upload.
              </p>
              {impact ? (
                <ul className="rounded-md border border-red-200 bg-red-50 p-4 list-disc pl-9 text-red-800 space-y-1">
                  <li><span className="font-mono font-semibold">{impact.to_delete_count}</span> newly-added products will be moved to Trash.</li>
                  <li><span className="font-mono font-semibold">{impact.to_revert_count}</span> updated products will be reverted to their previous state.</li>
                  {impact.manually_edited_after_upload > 0 && (
                    <li className="text-amber-800 font-medium">
                      ⚠ {impact.manually_edited_after_upload} of these products have been manually edited since this upload. Rolling back will discard those manual changes.
                    </li>
                  )}
                </ul>
              ) : (
                <div className="text-slate-400">Loading impact…</div>
              )}
              <p className="text-xs text-slate-400 italic">This action cannot be undone.</p>
            </div>
          )}
          {step === 2 && deleting && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Type the filename to confirm:</p>
              <p className="font-mono text-[#0D1B2A] break-all bg-[#FAF8F3] rounded p-2 text-xs">{deleting.filename}</p>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={deleting.filename} data-testid="delete-batch-filename-input" autoFocus />
            </div>
          )}
          <DialogFooter>
            {step === 1 && (
              <>
                <Button variant="outline" onClick={() => { setDeleting(null); setImpact(null); }}>Cancel</Button>
                <Button onClick={() => setStep(2)} disabled={!impact} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-batch-continue">Continue →</Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button variant="outline" onClick={() => setStep(1)} disabled={busy}>Back</Button>
                <Button onClick={confirmDelete} disabled={busy || !deleting || confirmText.trim() !== deleting.filename}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50" data-testid="delete-batch-confirm">
                  {busy ? "Deleting…" : "Delete Batch"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
